import { useState, useEffect, useCallback } from "react";
import { 
  Users, Mail, Phone, Calendar, Filter, Download, RefreshCw, 
  CheckCircle, Clock, XCircle, TrendingUp, MessageSquare,
  ChevronDown, Search, Eye, ExternalLink, Bell, BellRing, Volume2
} from "lucide-react";

const AdminDashboard = () => {
  const [contactLeads, setContactLeads] = useState([]);
  const [pdfLeads, setPdfLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("contact");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [lastLeadCount, setLastLeadCount] = useState(0);
  const [newLeadAlert, setNewLeadAlert] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Simple password protection (in production, use proper auth)
  const ADMIN_PASSWORD = "brandborn2026";

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem("admin_auth", "true");
    } else {
      setError("Incorrect password");
    }
  };

  useEffect(() => {
    // Check if already authenticated
    if (localStorage.getItem("admin_auth") === "true") {
      setIsAuthenticated(true);
    }
    
    // Check notification permission
    if ("Notification" in window && Notification.permission === "granted") {
      setNotificationsEnabled(true);
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        setNotificationsEnabled(true);
        new Notification("BrandBorn Notifications Enabled!", {
          body: "You'll now receive alerts for new leads",
          icon: "/images/logos/brandborn.png"
        });
      }
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkYyLiIWDgX1+gYOHiYqLi4uLiomHhYJ/fX1/goWIiouMjIuKiIaDgH59fX+ChYiKi4yMi4qIhYOAfn19f4KFiIqLjIyLioiFg4B+fX1/goWIiouMjIuKiIWDgH59fYCDhoiKi4yMi4qIhYKAfX19f4KFiIqLjIyLioiFg4B+fX2Ag4aIiouMjIuKiIWCgH19fX+ChYiKi4yMi4qIhYOAfn19gIOGiIqLjIyLioiFgoB9fX1/goWIiouMjIuKiIWDgH59fYCDhoiKi4yMi4qIhYKAfX19f4KFiIqLjIyLioiFg4B+fX2Ag4aIiouMjIuKiIWCgH19fX+ChYiKi4yMi4qIhYOAfn19gIOGiIqLjIyLioiFgoB9fX1/goWIiouMjIuKiIWDgH59fYCDhoiKi4yMi4qIhYKAfX19f4KFiIqLjIyLioiFg4B+fX2Ag4aIiouMjIuKiIWCgH19fX+ChYiKi4yMi4qIhYOAfn19");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  };

  // Send browser notification
  const sendBrowserNotification = (lead) => {
    if (notificationsEnabled && "Notification" in window) {
      const notification = new Notification("🔔 New Lead - BrandBorn!", {
        body: `${lead.name} (${lead.platform || "Contact Form"})\n📧 ${lead.email}\n📱 ${lead.phone}`,
        icon: "/images/logos/brandborn.png",
        tag: lead.id,
        requireInteraction: true
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
    playNotificationSound();
  };

  const fetchLeads = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      // Fetch contact form submissions
      const contactRes = await fetch(`${API_URL}/api/contact`);
      const contactData = await contactRes.json();
      
      // Check for new leads
      if (contactData.length > lastLeadCount && lastLeadCount > 0) {
        const newLeads = contactData.slice(0, contactData.length - lastLeadCount);
        newLeads.forEach(lead => sendBrowserNotification(lead));
        setNewLeadAlert(true);
        setTimeout(() => setNewLeadAlert(false), 5000);
      }
      setLastLeadCount(contactData.length);
      setContactLeads(contactData);

      // Fetch PDF download leads
      const pdfRes = await fetch(`${API_URL}/api/leads`);
      const pdfData = await pdfRes.json();
      setPdfLeads(pdfData);
    } catch (err) {
      console.error("Error fetching leads:", err);
    }
    
    if (showLoading) setIsLoading(false);
  }, [lastLeadCount, notificationsEnabled]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
      
      // Auto-refresh every 30 seconds
      let interval;
      if (autoRefresh) {
        interval = setInterval(() => fetchLeads(false), 30000);
      }
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [isAuthenticated, autoRefresh, fetchLeads]);

  // Generate WhatsApp message for a lead
  const getWhatsAppLink = (lead) => {
    const message = `Hi ${lead.name}! 👋

Thank you for contacting BrandBorn! 🎉

I noticed you're interested in ${lead.platform || "marketplace onboarding"}. 

I'm excited to help you launch your brand on India's top marketplaces!

Can we schedule a quick call to discuss your requirements?

Best regards,
BrandBorn Team
📞 +91 94646 45491
🌐 www.brandborn.in`;
    
    return `https://wa.me/${lead.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  };

  const updateLeadStatus = async (leadId, newStatus, type) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const endpoint = type === "contact" ? "contact" : "leads";
    
    try {
      await fetch(`${API_URL}/api/${endpoint}/${leadId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      
      // Update local state
      if (type === "contact") {
        setContactLeads(prev => 
          prev.map(lead => lead.id === leadId ? { ...lead, status: newStatus } : lead)
        );
      } else {
        setPdfLeads(prev => 
          prev.map(lead => lead.id === leadId ? { ...lead, status: newStatus } : lead)
        );
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const exportToCSV = (data, filename) => {
    const headers = Object.keys(data[0] || {}).join(",");
    const rows = data.map(item => 
      Object.values(item).map(val => `"${val}"`).join(",")
    ).join("\n");
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-blue-100 text-blue-700";
      case "contacted": return "bg-yellow-100 text-yellow-700";
      case "converted": return "bg-green-100 text-green-700";
      case "closed": return "bg-gray-100 text-gray-700";
      default: return "bg-blue-100 text-blue-700";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "new": return <Clock size={14} />;
      case "contacted": return <Phone size={14} />;
      case "converted": return <CheckCircle size={14} />;
      case "closed": return <XCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredContactLeads = contactLeads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredPdfLeads = pdfLeads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Enter password to access leads</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              className="w-full bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition"
            >
              Access Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Stats Cards
  const stats = [
    {
      label: "Total Contact Leads",
      value: contactLeads.length,
      icon: MessageSquare,
      color: "bg-blue-500"
    },
    {
      label: "PDF Downloads",
      value: pdfLeads.length,
      icon: Download,
      color: "bg-green-500"
    },
    {
      label: "New Leads",
      value: contactLeads.filter(l => l.status === "new" || !l.status).length,
      icon: Clock,
      color: "bg-yellow-500"
    },
    {
      label: "Converted",
      value: contactLeads.filter(l => l.status === "converted").length,
      icon: TrendingUp,
      color: "bg-emerald-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${newLeadAlert ? 'bg-green-500 animate-pulse' : 'bg-red-500'} rounded-lg flex items-center justify-center`}>
              {newLeadAlert ? <BellRing className="w-5 h-5 text-white" /> : <Users className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">BrandBorn Admin</h1>
              <p className="text-sm text-gray-500">
                {autoRefresh ? "🟢 Live - Auto-refreshing" : "⏸️ Paused"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Notification Toggle */}
            <button
              onClick={notificationsEnabled ? () => setNotificationsEnabled(false) : requestNotificationPermission}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                notificationsEnabled 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={notificationsEnabled ? "Notifications enabled" : "Enable notifications"}
            >
              {notificationsEnabled ? <BellRing size={18} /> : <Bell size={18} />}
              <span className="hidden sm:inline">{notificationsEnabled ? "Alerts On" : "Enable Alerts"}</span>
            </button>
            
            {/* Auto-refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                autoRefresh 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <RefreshCw size={18} className={autoRefresh ? "animate-spin" : ""} />
              <span className="hidden sm:inline">{autoRefresh ? "Live" : "Paused"}</span>
            </button>
            
            <button
              onClick={() => fetchLeads(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("admin_auth");
                setIsAuthenticated(false);
              }}
              className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
            >
              Logout
            </button>
          </div>
        </div>
        
        {/* New Lead Alert Banner */}
        {newLeadAlert && (
          <div className="bg-green-500 text-white px-4 py-2 text-center font-semibold animate-pulse">
            🎉 New Lead Received! Check the table below.
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-sm">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab("contact")}
              className={`flex-1 py-4 text-center font-semibold transition ${
                activeTab === "contact" 
                  ? "text-red-500 border-b-2 border-red-500" 
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Contact Form Leads ({contactLeads.length})
            </button>
            <button
              onClick={() => setActiveTab("pdf")}
              className={`flex-1 py-4 text-center font-semibold transition ${
                activeTab === "pdf" 
                  ? "text-red-500 border-b-2 border-red-500" 
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              PDF Download Leads ({pdfLeads.length})
            </button>
          </div>

          {/* Filters */}
          <div className="p-4 border-b flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            {activeTab === "contact" && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            )}
            
            <button
              onClick={() => exportToCSV(
                activeTab === "contact" ? filteredContactLeads : filteredPdfLeads,
                activeTab === "contact" ? "contact-leads" : "pdf-leads"
              )}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">Loading leads...</p>
              </div>
            ) : activeTab === "contact" ? (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Platform</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Message</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredContactLeads.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                        No leads found
                      </td>
                    </tr>
                  ) : (
                    filteredContactLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="font-semibold text-gray-900">{lead.name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <a href={`mailto:${lead.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                              <Mail size={14} /> {lead.email}
                            </a>
                            <a href={`tel:${lead.phone}`} className="text-sm text-gray-600 flex items-center gap-1">
                              <Phone size={14} /> {lead.phone}
                            </a>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {lead.platform || "Not specified"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-600 max-w-xs truncate" title={lead.message}>
                            {lead.message || "-"}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead.status || "new")}`}>
                            {getStatusIcon(lead.status || "new")}
                            {(lead.status || "new").charAt(0).toUpperCase() + (lead.status || "new").slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(lead.timestamp)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <select
                              value={lead.status || "new"}
                              onChange={(e) => updateLeadStatus(lead.id, e.target.value, "contact")}
                              className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-red-500"
                            >
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="converted">Converted</option>
                              <option value="closed">Closed</option>
                            </select>
                            <a
                              href={getWhatsAppLink(lead)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                              title="Message on WhatsApp"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Phone</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Source</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPdfLeads.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-12 text-center text-gray-500">
                        No PDF download leads found
                      </td>
                    </tr>
                  ) : (
                    filteredPdfLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="font-semibold text-gray-900">{lead.name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                            <Mail size={14} /> {lead.email}
                          </a>
                        </td>
                        <td className="px-4 py-4 text-gray-600">
                          {lead.phone ? (
                            <a href={`tel:${lead.phone}`} className="flex items-center gap-1">
                              <Phone size={14} /> {lead.phone}
                            </a>
                          ) : "-"}
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            {lead.source || "PDF Download"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(lead.timestamp)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="https://wa.me/919464645491"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition"
            >
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <ExternalLink className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-green-700">Open WhatsApp</span>
            </a>
            <a
              href="mailto:business@brandborn.in"
              className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
            >
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-blue-700">Send Email</span>
            </a>
            <button
              onClick={() => exportToCSV(contactLeads, "all-contact-leads")}
              className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition"
            >
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-purple-700">Export All Leads</span>
            </button>
            <a
              href="/"
              target="_blank"
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
            >
              <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-gray-700">View Website</span>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
